import TextTag from "components/cards/TextTag";
import { AbsoluteFill, Easing, Img, OffthreadVideo, Series, interpolate, staticFile, useCurrentFrame, useVideoConfig } from "remotion";
import { ReelMediaItem, isVideoMedia, mediaSrc, mediaStartAt, mediaUrl } from "./reelMedia";
const PhotoSequence = ({
	photos,
	city,
	nights,
	PHOTO_DURATION,
	journalTitle,
	showJournalTitle,
	scale,
	objectFit = "cover",
	isTemplate,
	dayIndex,
	lastDayIndex,
	isLandscape = false,
}) => {
	const photoFrame = useCurrentFrame();
	const { fps } = useVideoConfig();

	// Map user-friendly objectFit values to CSS object-fit values
	const getCssObjectFit = (userValue: string) => {
		const mapping = {
			fill: "cover", // Fill frame, may crop
			fit: "contain", // Maintain aspect ratio, show bars
			stretch: "fill", // Stretch to fill, may distort
		};
		return mapping[userValue] || userValue;
	};

	const cssObjectFit = getCssObjectFit(objectFit);

	const fade = interpolate(photoFrame, [0, 10], [0, 1], {
		extrapolateRight: "clamp",
	});

	const bounce = interpolate(photoFrame, [0, 12], [-100 * scale, 0], {
		easing: Easing.elastic(1.2),
		extrapolateRight: "clamp",
	});

	const onImageError = (e) => {
		e.preventDefault();
		e.target.onerror = null; // prevents looping
		e.target.src = staticFile("image-placeholder.png");
	};

	// For template trips: show "Day X" or "Day X - Y" (range) for multi-night stays
	// For regular trips: show "X nights" with moon icon or "Stopover"
	const getTemplateBadgeText = () => {
		if (!lastDayIndex || lastDayIndex === dayIndex) return `Day ${dayIndex}`;
		return `Day ${dayIndex} - ${lastDayIndex}`;
	};
	const badgeText = isTemplate ? getTemplateBadgeText() : nights === 0 ? "Stopover" : `${nights} night${nights > 1 ? "s" : ""}`;
	const badgeIcon = isTemplate ? "calendar" : nights === 0 ? undefined : "moon--outlined";

	return (
		<Series>
			{photos?.map((photo: ReelMediaItem, index) => (
				// `index` in the key, not the item: the same photo or clip can legitimately appear
				// twice in one destination, and an object item would stringify to [object Object].
				<Series.Sequence durationInFrames={PHOTO_DURATION} key={`${mediaSrc(photo)}-${index}`}>
					<AbsoluteFill>
						<div
							className="reel-title top-align"
							style={{
								opacity: fade,
								transform: `translateY(${bounce}px) scale(${scale})`,
								transformOrigin: "top",
								top: isLandscape ? 64 * scale : 192 * scale,
							}}
						>
							<div className="badge-container">
								<TextTag icon={badgeIcon} text={badgeText} color="black" />
							</div>
							<h1>
								<span>{showJournalTitle ? journalTitle || city : city}</span>
							</h1>
						</div>
					</AbsoluteFill>
					<AbsoluteFill className="photo-container" style={cssObjectFit === "contain" ? { backgroundColor: "#0f3e4a" } : undefined}>
						{isVideoMedia(photo) ? (
							// OffthreadVideo, not Video: FFmpeg frame extraction rather than Chrome's
							// media stack, the same reason the outro below uses it — <Video> needs HTTP
							// Range support from the bundle host and hangs at delayRender without it.
							// Muted because a reel's audio is chosen at publish time, and a stock clip
							// arriving with its own soundtrack is not something the editor can undo.
							<OffthreadVideo
								src={mediaUrl(photo)}
								startFrom={Math.round(mediaStartAt(photo) * fps)}
								muted
								style={{
									//@ts-ignore
									objectFit: cssObjectFit,
								}}
							/>
						) : (
							<Img
								referrerPolicy="no-referrer"
								src={mediaUrl(photo)}
								alt="tripImage"
								onError={onImageError}
								style={{
									//@ts-ignore
									objectFit: cssObjectFit,
								}}
							/>
						)}
					</AbsoluteFill>
				</Series.Sequence>
			))}
		</Series>
	);
};

export default PhotoSequence;
